import {useCallback, useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useData} from "../../../hooks/useData";
import {useWatcher} from "../../../hooks/useWatcher";
import {doFetch} from "../../../util/fetch";
import {GetAccountResponse, PortalSessionResponse, Subscription} from "./account-interfaces";
import {getPlanByProductId} from "../../../pricing";

// Subscriptions Paddle lets the customer self-manage from the portal.
const MANAGEABLE_STATUSES = ["active", "trialing"];

// Friendly labels for the backend SubscriptionTier enum (see Lib/Subscriptions/SubscriptionTier.cs).
const TIER_LABELS: Record<string, string> = {
    FREE: "Free",
    T1: "Personal",
    T2: "Team",
    T3: "Organization",
    DEVELOPER: "Developer",
};

// Turns raw Paddle product/price IDs into a plan name and billing cycle for display.
function describeSubscription(productId?: string, priceId?: string): { plan: string; billing?: string } {
    const plan = getPlanByProductId(productId);
    const planName = plan?.name ?? productId ?? "Unknown plan";
    let billing: string | undefined;
    if (plan && priceId === plan.monthly) billing = "Monthly";
    else if (plan && priceId === plan.yearly) billing = "Annual";
    return {plan: planName, billing};
}

function formatDate(unixSeconds: number): string {
    return new Date(unixSeconds * 1000).toLocaleDateString();
}

// When a subscription renews or ends, shown under each subscription. Returns null when there's nothing to show.
function subscriptionSchedule(sub: Subscription): { label: string; date: string; warn: boolean } | null {
    if (sub.scheduledChangeAction === "cancel" && sub.scheduledChange > 0) {
        return {label: "Active until", date: formatDate(sub.scheduledChange), warn: true};
    }
    if (sub.scheduledChangeAction === "pause" && sub.scheduledChange > 0) {
        return {label: "Pauses on", date: formatDate(sub.scheduledChange), warn: true};
    }
    if (MANAGEABLE_STATUSES.includes(sub.status.toLowerCase()) && sub.currentPeriodEnd > 0) {
        return {label: "Renews on", date: formatDate(sub.currentPeriodEnd), warn: false};
    }
    return null;
}

// Extracts a human-readable browser and OS from a raw user agent string,
// so we don't expose the full UA to the user.
function parseUserAgent(userAgent: string): { browser: string; os: string } {
    let os = "Unknown OS";
    if (/windows nt/i.test(userAgent)) os = "Windows";
    else if (/iphone|ipad|ipod/i.test(userAgent)) os = "iOS";
    else if (/mac os x/i.test(userAgent)) os = "macOS";
    else if (/android/i.test(userAgent)) os = "Android";
    else if (/linux/i.test(userAgent)) os = "Linux";

    // Order matters: Edge/Opera/Chrome UAs all contain "Safari", and Edge/Opera contain "Chrome".
    let browser = "Unknown Browser";
    if (/edg\//i.test(userAgent)) browser = "Edge";
    else if (/opr\/|opera/i.test(userAgent)) browser = "Opera";
    else if (/firefox\//i.test(userAgent)) browser = "Firefox";
    else if (/chrome\//i.test(userAgent)) browser = "Chrome";
    else if (/safari\//i.test(userAgent)) browser = "Safari";

    return {browser, os};
}

function SubscriptionStatusBadge({status}: { status: string }) {
    const colorMap: Record<string, string> = {
        active: "bg-green-600 text-green-100",
        trialing: "bg-blue-600 text-blue-100",
        past_due: "bg-yellow-600 text-yellow-100",
        canceled: "bg-red-700 text-red-100",
        cancelled: "bg-red-700 text-red-100",
        paused: "bg-neutral-500 text-neutral-200",
        unpaid: "bg-orange-600 text-orange-100",
    };
    const classes = colorMap[status.toLowerCase()] ?? "bg-neutral-500 text-neutral-200";
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${classes}`}>{status}</span>;
}

export default function Account() {
    const [formData, setFormData] = useState<GetAccountResponse>()

    const data = useData<GetAccountResponse>(`/api/client/account`)
    useWatcher(data.data, d => {
        if (d) {
            console.log("update form data", d)
            setFormData(d)
        }
    })

    const [params, setParams] = useSearchParams()

    const [refreshing, setRefreshing] = useState(false)
    const refreshAccount = useCallback(async () => {
        setRefreshing(true)
        const res = await doFetch<GetAccountResponse>(`/api/client/account`)
        if (res.ok && res.data) {
            setFormData(res.data)
        }
        setRefreshing(false)
    }, [])

    // Returning from a successful checkout (?msg=sub_success): the provisioning webhook can lag a few
    // seconds behind the redirect, so refresh once after a short delay to pick up the new subscription.
    const autoRefreshed = useRef(false)
    useEffect(() => {
        if (autoRefreshed.current || params.get("msg") !== "sub_success") {
            return
        }
        autoRefreshed.current = true

        setParams(prev => {
            const next = new URLSearchParams(prev)
            next.delete("msg")
            return next
        }, {replace: true})

        const timer = setTimeout(() => { refreshAccount() }, 5000)
        return () => clearTimeout(timer)
    }, [params, setParams, refreshAccount])

    const handleChange = (field: string, value: any) => {
        setFormData(prev => prev ? {...prev, [field]: value} : prev);
    };

    const removeConnection = (platform: 'discord' | 'steam') => {
        alert("Not implemented yet")
    };

    // Paddle portal deep links are single-use and must not be cached, so we mint a fresh session per click
    // and open it in a new tab. portalBusy holds the key of the action currently loading.
    const [portalBusy, setPortalBusy] = useState<string | null>(null);

    const openPortal = async (action: 'overview' | 'cancel' | 'update', subscriptionId?: string) => {
        setPortalBusy(subscriptionId ? `${action}:${subscriptionId}` : action);
        // Open the tab synchronously within the click so popup blockers don't reject it after the async
        // fetch, then point it at the portal URL once we have it. (noopener can't be used here: it makes
        // window.open return null, and we need the handle to navigate the tab.)
        const portalTab = window.open('about:blank', '_blank');
        if (portalTab) {
            // Sever the back-reference so the Paddle page can't touch this tab via window.opener.
            portalTab.opener = null;
        }
        try {
            const res = await doFetch<PortalSessionResponse>(`/api/client/account/portal-session`, {
                method: "POST"
            });
            if (!res.ok || !res.data) {
                portalTab?.close();
                alert(res.errorString ?? "Failed to open the billing portal. Please try again.");
                return;
            }

            let url: string | undefined;
            if (action === 'overview') {
                url = res.data.overviewUrl;
            } else {
                const match = res.data.subscriptions.find(s => s.id === subscriptionId);
                url = action === 'cancel' ? match?.cancelUrl : match?.updatePaymentMethodUrl;
            }

            if (!url) {
                portalTab?.close();
                alert("This subscription can no longer be managed.");
                return;
            }

            if (portalTab) {
                portalTab.location.href = url;
            } else {
                // Popup was blocked; fall back to navigating the current tab.
                window.location.href = url;
            }
        } finally {
            setPortalBusy(null);
        }
    };

    const revokeSession = async (sessionId: string) => {
        const result = await doFetch(`/api/client/account/session?sessionId=${encodeURIComponent(sessionId)}`, {
            method: "DELETE"
        });
        if (result.ok) {
            setFormData(prev => prev
                ? {...prev, sessions: prev.sessions.filter(s => s.id !== sessionId)}
                : prev);
        } else {
            alert(result.errorString ?? "Failed to revoke session");
        }
    };

    if (data.isLoading || !formData) return <div className="p-4">Loading...</div>;

    return <div className="max-w-4xl mx-auto p-6 w-full">
        <div className="space-y-4 mb-10">
            <h1 className="text-2xl font-bold">Account Settings</h1>

            <fieldset className="border rounded p-6 bg-neutral-800 text-white">
                <legend className="px-2 text-lg font-semibold">General</legend>

                <div className="space-y-2">
                    <label className="block">Name</label>
                    <input disabled className="w-full p-2 border rounded bg-neutral-700 text-white" value={formData.name}
                           onChange={e => handleChange('name', e.target.value)}/>

                    <label className="block">Email</label>
                    <input disabled className="w-full p-2 border rounded bg-neutral-700 text-white" value={formData.email}
                           onChange={e => handleChange('email', e.target.value)}/>
                </div>


                <div className="mt-4 flex justify-between">
                    <p className="text-sm text-white-t mt-4">
                        Account created {new Date(formData.memberSince * 1000).toLocaleDateString()}</p>
                    {/*<button onClick={handleSubmit}*/}
                    {/*        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">*/}
                    {/*    Save*/}
                    {/*</button>*/}
                </div>
            </fieldset>

            <fieldset className="border rounded p-6 bg-neutral-800 text-white space-y-4">
                <legend className="px-2 text-lg font-semibold">Subscription</legend>

                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p><strong>Current Plan:</strong> {TIER_LABELS[formData.userTier] ?? formData.userTier}</p>
                    <div className="flex gap-2">
                        <button onClick={refreshAccount} disabled={refreshing}
                                className="px-4 py-2 bg-neutral-600 text-white rounded hover:bg-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed">
                            {refreshing ? 'Refreshing…' : 'Refresh'}
                        </button>
                        {formData.subscriptions && formData.subscriptions.length > 0 && (
                            <button onClick={() => openPortal('overview')} disabled={portalBusy !== null}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {portalBusy === 'overview' ? 'Opening…' : 'Manage billing'}
                            </button>
                        )}
                    </div>
                </div>

                {formData.subscriptions && formData.subscriptions.length > 0 && (
                    <div className="space-y-3">
                        {formData.subscriptions.map(sub => {
                            const manageable = MANAGEABLE_STATUSES.includes(sub.status.toLowerCase());
                            const {plan, billing} = describeSubscription(sub.productId, sub.priceId);
                            const schedule = subscriptionSchedule(sub);
                            const scheduledToCancel = sub.scheduledChangeAction === "cancel";
                            return (
                                <div key={sub.id} className="bg-neutral-700 rounded-lg p-4 border border-neutral-600">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <span className="text-base font-semibold text-neutral-100 truncate">{plan}</span>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {scheduledToCancel && (
                                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-600/80 text-amber-50">
                                                    Scheduled to cancel
                                                </span>
                                            )}
                                            <SubscriptionStatusBadge status={sub.status}/>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2">
                                        {billing && (
                                            <>
                                                <span className="text-neutral-400">Billing</span>
                                                <span className="text-neutral-200">{billing}</span>
                                            </>
                                        )}
                                        {schedule && (
                                            <>
                                                <span className="text-neutral-400">{schedule.label}</span>
                                                <span className={schedule.warn ? "text-amber-300" : "text-neutral-200"}>{schedule.date}</span>
                                            </>
                                        )}
                                        <span className="text-neutral-400">Last updated</span>
                                        <span className="text-neutral-200">{new Date(sub.lastUpdated * 1000).toLocaleDateString()}</span>
                                    </div>
                                    {manageable && (
                                        <div className="flex flex-wrap gap-2 mt-4">
                                            <button onClick={() => openPortal('update', sub.id)} disabled={portalBusy !== null}
                                                    className="px-3 py-1 text-sm bg-neutral-600 text-white rounded hover:bg-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                                {portalBusy === `update:${sub.id}` ? 'Opening…' : 'Update payment method'}
                                            </button>
                                            <button onClick={() => openPortal('cancel', sub.id)} disabled={portalBusy !== null}
                                                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                                {portalBusy === `cancel:${sub.id}` ? 'Opening…' : 'Cancel subscription'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </fieldset>
        </div>

        <div className="space-y-4 mb-10">
            <h2 className="text-2xl font-bold">Connections</h2>

            <fieldset className="border rounded p-6 bg-neutral-800 text-white">
                <legend className="px-2 text-lg font-semibold">Discord</legend>
                {formData.discord ? (
                    <>
                        <div className="flex items-center space-x-4">
                            {formData.discord.avatarUrl && <img src={formData.discord.avatarUrl} alt="Discord Avatar"
                                                                className="w-12 h-12 rounded-full"/>}
                            <div>
                                <p><strong>Display Name:</strong> {formData.discord.displayName}</p>
                                <p><strong>Email:</strong> {formData.discord.email}</p>
                            </div>
                        </div>
                        <div className="mt-4 text-right">
                            <button disabled onClick={() => removeConnection('discord')}
                                    className="px-3 py-1 bg-neutral-600 text-neutral-300 rounded">
                                Remove
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-6">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Link Account
                        </button>
                    </div>
                )}
            </fieldset>

            <fieldset className="border rounded p-6 bg-neutral-800 text-white">
                <legend className="px-2 text-lg font-semibold">Steam</legend>
                {formData.steam ? (
                    <>
                        <div className="flex items-center space-x-4">
                            {formData.steam.avatarUrl &&
                                <img src={formData.steam.avatarUrl} alt="Steam Avatar"
                                     className="w-12 h-12 rounded-full"/>}
                            <div>
                                <p><strong>Steam ID:</strong> {formData.steam.userId}</p>
                                <p><strong>Display Name:</strong> {formData.steam.displayName}</p>
                            </div>
                        </div>
                        <div className="mt-4 text-right">
                            <button onClick={() => removeConnection('steam')}
                                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600">
                                Remove
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-6">
                        <a href={formData.steamLinkUrl}
                           className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Link Account</a>
                    </div>
                )}
            </fieldset>
        </div>

        <div className="space-y-4 mb-10">
            <h2 className="text-2xl font-bold">Sessions</h2>

            <fieldset className="border rounded p-6 bg-neutral-800 text-white space-y-4">
                <legend className="px-2 text-lg font-semibold">Active Sessions</legend>

                {formData.sessions && formData.sessions.length > 0 ? (
                    <div className="space-y-3">
                        {formData.sessions.map(session => {
                            const {browser, os} = parseUserAgent(session.userAgent);
                            return (
                                <div key={session.id}
                                     className="bg-neutral-700 rounded-lg p-4 border border-neutral-600">
                                    <div className="flex items-start justify-between gap-4">
                                        <p className="text-sm font-semibold text-neutral-100">{os} / {browser}</p>
                                        <button onClick={() => revokeSession(session.id)}
                                                className="shrink-0 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600">
                                            Sign out
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2">
                                        {session.ip && (
                                            <>
                                                <span className="text-neutral-400">IP Address</span>
                                                <span className="font-mono text-neutral-200 truncate">{session.ip}</span>
                                            </>
                                        )}
                                        {session.location && (
                                            <>
                                                <span className="text-neutral-400">Location</span>
                                                <span className="text-neutral-200 truncate">{session.location}</span>
                                            </>
                                        )}
                                        <span className="text-neutral-400">Last active</span>
                                        <span className="text-neutral-200">{new Date(session.lastActiveTime * 1000).toLocaleString()}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-neutral-400">No active sessions.</p>
                )}
            </fieldset>
        </div>
    </div>
}